import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Shield,
  BarChart3, Activity, Building2, FileText, Download,
  Loader2, RefreshCw, Database
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ZoneConfidenceCard, PriceElasticityCard, DataIntegrityFooter, CredibleMetric } from '@/components/DataCredibility';
import AEDIcon from '@/components/icons/AEDIcon';

const VERDICT_STYLES = {
  'STRONG_FUNDAMENTALS': { bg: 'bg-green-600/20', text: 'text-green-400', border: 'border-green-600/30', icon: CheckCircle2 },
  'STRONG_INVESTMENT': { bg: 'bg-green-600/20', text: 'text-green-400', border: 'border-green-600/30', icon: CheckCircle2 }, // Legacy
  'SOLID_POSITION': { bg: 'bg-emerald-600/20', text: 'text-emerald-400', border: 'border-emerald-600/30', icon: TrendingUp },
  'ATTRACTIVE_OPPORTUNITY': { bg: 'bg-emerald-600/20', text: 'text-emerald-400', border: 'border-emerald-600/30', icon: TrendingUp }, // Legacy
  'CONDITIONAL_FIT': { bg: 'bg-gold-600/20', text: 'text-gold-400', border: 'border-gold-600/30', icon: AlertTriangle },
  'MONITOR': { bg: 'bg-red-600/20', text: 'text-red-400', border: 'border-red-600/30', icon: AlertTriangle }
};

const SEVERITY_COLORS = {
  'HIGH': 'bg-red-600/20 text-red-400 border-red-600/30',
  'MEDIUM': 'bg-gold-600/20 text-gold-400 border-gold-600/30',
  'LOW': 'bg-blue-600/20 text-blue-400 border-blue-600/30'
};

export const MarketIntelligenceReport = ({ marketIntelligence, propertyTitle, propertyId, loading = false }) => {
  const navigate = useNavigate();
  if (loading) {
    return <MarketIntelligenceLoader />;
  }
  
  if (!marketIntelligence) return null;
  
  const {
    area_benchmark_psf,
    comparable_range,
    demand_liquidity,
    risk_flags,
    rashii_verdict,
    price_analysis,
    rental_projections,
    roi_projections,
    comparables
  } = marketIntelligence;

  const verdictStyle = VERDICT_STYLES[rashii_verdict?.verdict] || VERDICT_STYLES['MONITOR'];
  const VerdictIcon = verdictStyle.icon;

  return (
    <Card className="glass-card border-white/10" data-testid="market-intelligence-report">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Market Intelligence Report
          </CardTitle>
          <Badge className={`${verdictStyle.bg} ${verdictStyle.text} ${verdictStyle.border}`}>
            <VerdictIcon className="w-3 h-3 mr-1" />
            {rashii_verdict?.verdict?.replace('_', ' ')}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{rashii_verdict?.summary}</p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-secondary/30">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
            <TabsTrigger value="projections">Projections</TabsTrigger>
            <TabsTrigger value="risks">Risks</TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                label="Area Benchmark"
                value={`AED ${area_benchmark_psf?.toLocaleString()}`}
                sublabel="per sqft"
                icon={<Building2 className="w-4 h-4" />}
              />
              <MetricCard
                label="Comparable Range"
                value={`${comparable_range?.min?.toLocaleString()} - ${comparable_range?.max?.toLocaleString()}`}
                sublabel="AED/sqft"
                icon={<BarChart3 className="w-4 h-4" />}
              />
              <MetricCard
                label="Liquidity Score"
                value={demand_liquidity?.score || 0}
                sublabel={demand_liquidity?.rating}
                icon={<Activity className="w-4 h-4" />}
                isScore
              />
              <MetricCard
                label="Price Position"
                value={`${price_analysis?.price_vs_market_pct > 0 ? '+' : ''}${price_analysis?.price_vs_market_pct}%`}
                sublabel={price_analysis?.price_position?.replace('_', ' ')}
                icon={price_analysis?.price_vs_market_pct < 0 ? <TrendingDown className="w-4 h-4 text-green-400" /> : <TrendingUp className="w-4 h-4" />}
              />
            </div>
            
            {/* Key Insights */}
            {rashii_verdict?.key_insights?.length > 0 && (
              <div className="p-4 rounded border border-primary/30 bg-primary/5">
                <div className="text-xs uppercase tracking-widest text-primary mb-3">Key Insights</div>
                <div className="space-y-2">
                  {rashii_verdict.key_insights.map((insight, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{insight}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Demand/Liquidity Factors */}
            <div className="p-4 rounded border border-white/10 bg-secondary/20">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
                Liquidity Assessment
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm">Liquidity Score</span>
                <div className="flex items-center gap-2">
                  <Progress value={demand_liquidity?.score || 0} className="w-24 h-2" />
                  <Badge variant="outline" className={
                    demand_liquidity?.rating === 'HIGH' ? 'border-green-600/30 text-green-400' :
                    demand_liquidity?.rating === 'MODERATE' ? 'border-gold-600/30 text-gold-400' :
                    'border-red-600/30 text-red-400'
                  }>
                    {demand_liquidity?.rating}
                  </Badge>
                </div>
              </div>
              <div className="space-y-1">
                {demand_liquidity?.factors?.map((factor, idx) => (
                  <p key={idx} className="text-xs text-muted-foreground">• {factor}</p>
                ))}
              </div>
            </div>
          </TabsContent>
          
          {/* Pricing Tab */}
          <TabsContent value="pricing" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Price Analysis */}
              <div className="p-4 rounded border border-white/10 bg-secondary/20">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
                  Price Analysis
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Asking Price</span>
                    <span className="font-medium">AED {price_analysis?.asking_price?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Price/sqft</span>
                    <span className="font-medium">AED {price_analysis?.price_per_sqft?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Market Avg/sqft</span>
                    <span className="font-medium">AED {price_analysis?.market_avg_per_sqft?.toLocaleString()}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">vs Market</span>
                    <Badge className={price_analysis?.price_vs_market_pct < 0 ? 'bg-green-600/20 text-green-400' : 'bg-gold-600/20 text-gold-400'}>
                      {price_analysis?.price_vs_market_pct > 0 ? '+' : ''}{price_analysis?.price_vs_market_pct}%
                    </Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-white/10">
                  {price_analysis?.insight}
                </p>
              </div>
              
              {/* Comparables */}
              <div className="p-4 rounded border border-white/10 bg-secondary/20">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
                  Market Comparables
                </div>
                <div className="space-y-2">
                  {comparables?.slice(0, 4).map((comp, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm">
                      <div className="flex-1 truncate pr-2">
                        <span className="text-muted-foreground">{comp.property}</span>
                        <span className="text-xs text-muted-foreground/60 block">{comp.size_sqft} sqft</span>
                      </div>
                      <span className="font-medium">AED {comp.price_per_sqft?.toLocaleString()}/sqft</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-white/10 flex justify-between text-xs">
                  <span className="text-muted-foreground">Range</span>
                  <span className="text-primary font-medium">
                    AED {comparable_range?.min?.toLocaleString()} - {comparable_range?.max?.toLocaleString()}/sqft
                  </span>
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Projections Tab */}
          <TabsContent value="projections" className="space-y-4 mt-4">
            {/* Rental Projections */}
            <div className="p-4 rounded border border-white/10 bg-secondary/20">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                Rental Income Projections
              </div>
              <div className="grid grid-cols-3 gap-4">
                <ProjectionCard
                  label="Conservative"
                  annual={rental_projections?.conservative?.annual_rent}
                  monthly={rental_projections?.conservative?.monthly_rent}
                  yield_pct={rental_projections?.conservative?.yield_pct}
                  variant="conservative"
                />
                <ProjectionCard
                  label="Market Rate"
                  annual={rental_projections?.market_rate?.annual_rent}
                  monthly={rental_projections?.market_rate?.monthly_rent}
                  yield_pct={rental_projections?.market_rate?.yield_pct}
                  variant="market"
                />
                <ProjectionCard
                  label="Premium"
                  annual={rental_projections?.premium_positioning?.annual_rent}
                  monthly={rental_projections?.premium_positioning?.monthly_rent}
                  yield_pct={rental_projections?.premium_positioning?.yield_pct}
                  variant="premium"
                />
              </div>
            </div>
            
            {/* ROI Projections */}
            <div className="p-4 rounded border border-primary/30 bg-primary/5">
              <div className="text-xs uppercase tracking-widest text-primary mb-4">
                Total ROI Projection
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-display text-primary">{roi_projections?.total_roi_conservative}</div>
                  <div className="text-xs text-muted-foreground mt-1">Conservative (Annual)</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-display text-green-400">{roi_projections?.total_roi_optimistic}</div>
                  <div className="text-xs text-muted-foreground mt-1">Optimistic (Annual)</div>
                </div>
              </div>
              <Separator className="my-4" />
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Rental Yield Range</span>
                  <span>{roi_projections?.rental_yield_range}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Capital Appreciation</span>
                  <span>{roi_projections?.capital_appreciation_range}</span>
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Risks Tab */}
          <TabsContent value="risks" className="space-y-4 mt-4">
            {risk_flags?.length > 0 ? (
              <div className="space-y-3">
                {risk_flags.map((risk, idx) => (
                  <div key={idx} className="p-4 rounded border border-white/10 bg-secondary/20">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        risk.severity === 'HIGH' ? 'text-red-400' :
                        risk.severity === 'MEDIUM' ? 'text-gold-400' : 'text-blue-400'
                      }`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{risk.type.replace('_', ' ')}</span>
                          <Badge className={SEVERITY_COLORS[risk.severity]}>
                            {risk.severity}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{risk.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 rounded border border-green-600/30 bg-green-600/10 text-center">
                <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-green-400 font-medium">No significant risk factors identified</p>
                <p className="text-sm text-muted-foreground mt-1">Property has clean fundamentals</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Export Button */}
        <Separator />
        <div className="flex justify-end">
          <Button 
            variant="outline" 
            className="border-primary/50 hover:bg-primary hover:text-primary-foreground"
            onClick={() => propertyId && navigate(`/deal-card/${propertyId}`)}
            data-testid="export-deal-card-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Deal Card
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Sub-components
const MetricCard = ({ label, value, sublabel, icon, isScore = false }) => (
  <div className="p-3 rounded border border-white/10 bg-secondary/20">
    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
      {icon}
      <span>{label}</span>
    </div>
    <div className="text-lg font-display text-primary">{value}</div>
    <div className="text-xs text-muted-foreground">{sublabel}</div>
    {isScore && <Progress value={value} className="h-1 mt-2" />}
  </div>
);

const ProjectionCard = ({ label, annual, monthly, yield_pct, variant }) => (
  <div className={`p-3 rounded text-center ${
    variant === 'premium' ? 'border border-primary/30 bg-primary/10' :
    variant === 'market' ? 'border border-white/20 bg-secondary/30' :
    'border border-white/10 bg-secondary/10'
  }`}>
    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{label}</div>
    <div className="text-lg font-display">{yield_pct}%</div>
    <div className="text-xs text-muted-foreground">yield</div>
    <Separator className="my-2" />
    <div className="text-sm font-medium">AED {annual?.toLocaleString()}</div>
    <div className="text-xs text-muted-foreground">per year</div>
  </div>
);

// Loading component for AI latency handling with multi-stage UX
export const MarketIntelligenceLoader = ({ message = "Generating Intelligence…" }) => {
  const [loadingMessage, setLoadingMessage] = useState(message);
  const [subMessage, setSubMessage] = useState("Analyzing market data and generating personalized insights...");
  const [elapsed, setElapsed] = useState(0);
  const [stage, setStage] = useState(1); // 1: initial, 2: >10s, 3: >30s
  
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed(prev => {
        const newElapsed = prev + 1;
        
        // Update messages based on elapsed time per user requirements:
        // >10s: "Generating Intelligence..."
        // >30s: "Still working..."
        if (newElapsed > 30) {
          setStage(3);
          setLoadingMessage("Still working…");
          setSubMessage("Running comprehensive analysis. This may take up to 60 seconds for accurate results.");
        } else if (newElapsed > 10) {
          setStage(2);
          setLoadingMessage("Generating Intelligence…");
          setSubMessage("Analyzing comparable properties and market benchmarks...");
        }
        
        return newElapsed;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  return (
    <Card className="glass-card border-white/10" data-testid="market-intelligence-loading">
      <CardContent className="p-8">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-primary" />
            </div>
          </div>
          <div className="text-center space-y-2">
            <p className="text-sm font-medium" data-testid="loader-message">{loadingMessage}</p>
            <p className="text-xs text-muted-foreground max-w-xs">
              {subMessage}
            </p>
          </div>
          <div className="w-64 space-y-1">
            <Progress value={Math.min((elapsed / 60) * 100, 95)} className="h-1" />
            <p className="text-xs text-center text-muted-foreground">{elapsed}s elapsed</p>
          </div>
          
          {/* Warning for long delays */}
          {stage === 3 && elapsed > 45 && (
            <p className="text-xs text-gold-400 text-center animate-pulse">
              Almost there — completing final analysis...
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Error state component with retry button
export const MarketIntelligenceError = ({ onRetry }) => (
  <Card className="glass-card border-red-600/30" data-testid="market-intelligence-error">
    <CardContent className="p-6">
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="p-3 rounded-full bg-red-600/20 flex-shrink-0">
          <AlertTriangle className="w-6 h-6 text-red-400" />
        </div>
        <div className="flex-1 text-center sm:text-left">
          <p className="font-medium text-red-400" data-testid="error-title">Report generation failed</p>
          <p className="text-sm text-muted-foreground mt-1">Unable to generate market intelligence. Please try again.</p>
        </div>
        <Button 
          onClick={onRetry} 
          variant="outline" 
          className="border-primary/50 hover:bg-primary hover:text-primary-foreground"
          data-testid="retry-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    </CardContent>
  </Card>
);

export default MarketIntelligenceReport;
